// The clinical report feature's rules, kept free of React and the DOM so they can be
// pinned down by clinicalReportRules.test.js: the form's state and the body it
// sends, what stops it being saved, how a record reads, and the list's filters.
// Used by pages/ClinicalReports.jsx and components/ClinicalReportDialog.jsx.
//
// Which notifications a report calls for is *not* here: the backend derives them
// (core/clinical_reports.py) and serves them on every read, with their due times and
// states, so there is one implementation of those rules.
//
// Imports nothing but feedbackRules.js (itself constants.js only), so it runs under
// Jest as-is.
import { nameKey } from "./feedbackRules";

const blank = (v) => !String(v ?? "").trim();
const trimmed = (v) => (blank(v) ? null : String(v).trim());

// <input type="datetime-local"> works in the browser's zone; convert to and from ISO.
export const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
export const fromLocalInput = (s) => (s ? new Date(s).toISOString() : null);

export const AFFECTED_FIELDS = [
  "affected_name", "affected_race", "affected_race_number", "affected_age",
  "affected_phone", "affected_email",
];

export const emptyReportForm = () => ({
  event_id: "",
  report_type: "",
  type_other: "",
  occurred_at: "",        // datetime-local; blank on a new report is "now"
  details: "",
  harm: "",
  participant_id: null,
  ...Object.fromEntries(AFFECTED_FIELDS.map(f => [f, ""])),
  incident_id: null,
  candour_at: "",         // datetime-local
  candour_by: "",
  status: "Created",
});

/** A report as served, in form state. */
export const reportToForm = (r) => ({
  ...emptyReportForm(),
  event_id: r.event_id ?? "",
  report_type: r.report_type || "",
  type_other: r.type_other || "",
  occurred_at: toLocalInput(r.occurred_at),
  details: r.details || "",
  harm: r.harm || "",
  participant_id: r.participant_id ?? null,
  ...Object.fromEntries(AFFECTED_FIELDS.map(f => [f, r[f] ?? ""])),
  incident_id: r.incident_id ?? null,
  candour_at: toLocalInput(r.candour_at),
  candour_by: r.candour_by || "",
  status: r.status || "Created",
});

/** The affected-person fields copied from an entrant (the entry list's snapshot). */
export const affectedFromParticipant = (p) => ({
  participant_id: p.id,
  affected_name: p.name ?? "",
  affected_race: p.race ?? "",
  affected_race_number: p.race_number ?? "",
  affected_age: p.age ?? "",
  affected_phone: p.phone ?? "",
  affected_email: p.email ?? "",
});

/** The affected-person fields copied from an incident's patient. */
export const affectedFromIncident = (i) => ({
  participant_id: i.patient_participant_id ?? null,
  affected_name: i.patient_name ?? "",
  affected_race: i.patient_race ?? "",
  affected_race_number: i.patient_race_number ?? "",
  affected_age: i.patient_age ?? "",
  affected_phone: i.patient_phone ?? "",
  affected_email: i.patient_email ?? "",
});

/** What stops the form being saved, as sentences — empty when it may be. */
export const reportProblems = (form) => {
  const problems = [];
  if (blank(form.report_type)) problems.push("Choose the type of report.");
  else if (form.report_type === "Other" && blank(form.type_other)) {
    problems.push("Describe the type of report.");
  }
  if (blank(form.details)) problems.push("Give the details.");
  if (!blank(form.candour_at) && blank(form.candour_by)) {
    problems.push("Say who told the patient.");
  }
  return problems;
};

/** The body POSTed / PUT to /clinical-reports/. */
export const reportBody = (form) => {
  const age = blank(form.affected_age) ? null : Number(form.affected_age);
  const hasEvent = !blank(form.event_id);
  return {
    event_id: hasEvent ? Number(form.event_id) : null,
    report_type: form.report_type,
    type_other: form.report_type === "Other" ? trimmed(form.type_other) : null,
    occurred_at: fromLocalInput(form.occurred_at),
    details: trimmed(form.details),
    harm: form.harm || null,
    // An entrant and an incident belong to an event; with none, neither can be.
    participant_id: hasEvent ? form.participant_id ?? null : null,
    affected_name: trimmed(form.affected_name),
    affected_race: trimmed(form.affected_race),
    affected_race_number: trimmed(form.affected_race_number),
    affected_age: Number.isFinite(age) ? age : null,
    affected_phone: trimmed(form.affected_phone),
    affected_email: trimmed(form.affected_email),
    incident_id: hasEvent ? form.incident_id ?? null : null,
    candour_at: fromLocalInput(form.candour_at),
    candour_by: blank(form.candour_at) ? null : trimmed(form.candour_by),
    status: form.status || "Created",
  };
};

// --- Showing a record --------------------------------------------------------

export const reportTypeText = (r) =>
  (r?.report_type === "Other" && r.type_other ? `Other: ${r.type_other}` : r?.report_type || "");

/** The affected person, as the list shows them: name, then race number. */
export const affectedText = (r) =>
  [r?.affected_name || (r?.participant_label ? r.participant_label : ""),
   r?.affected_race_number && `#${r.affected_race_number}`]
    .filter(Boolean).join(" ");

export const NOTIFICATION_STATE_TEXT = {
  done: "Done",
  not_needed: "Not needed",
  overdue: "Overdue",
  due: "Due",
};
export const NOTIFICATION_STATE_COLOR = {
  done: "success",
  not_needed: "default",
  overdue: "error",
  due: "warning",
};

const outstanding = (n) => n.state === "due" || n.state === "overdue";

/** `{ overdue, outstanding }` counts over a report's notifications. */
export const notificationCounts = (r) => {
  const ns = r?.notifications || [];
  return {
    overdue: ns.filter(n => n.state === "overdue").length,
    outstanding: ns.filter(outstanding).length,
  };
};

/** One line per notification, for a spreadsheet cell. */
export const notificationsText = (r) => (r?.notifications || []).map(n => {
  const state = NOTIFICATION_STATE_TEXT[n.state] || n.state;
  const when = n.state === "done" && n.done_at
    ? ` ${new Date(n.done_at).toLocaleString()}${n.done_by ? ` by ${n.done_by}` : ""}`
    : n.state === "not_needed" && n.not_needed_reason ? `: ${n.not_needed_reason}`
      : ` by ${new Date(n.due_at).toLocaleString()}`;
  return `${n.to} — ${n.what} (${n.who}) — ${state}${when}`;
}).join("\n");

// --- The list's filters --------------------------------------------------------

export const NO_EVENT = "none";
export const NOTIFICATION_FILTERS = [
  ["overdue", "Overdue"],
  ["outstanding", "Outstanding"],
];

export const emptyReportFilters = () => ({
  event: "",          // an event id, NO_EVENT, or ""
  type: "",
  status: "",
  notifications: "",  // one of NOTIFICATION_FILTERS, or ""
  search: "",
});

/** The rows the filters leave. `search` reads the affected person's name and the linked entrant. */
export const filterReports = (rows, f) => {
  const q = nameKey(f.search);
  return (rows || []).filter(r => {
    if (f.event === NO_EVENT) { if (r.event_id != null) return false; }
    else if (f.event && String(r.event_id) !== String(f.event)) return false;
    if (f.type && r.report_type !== f.type) return false;
    if (f.status && r.status !== f.status) return false;
    if (f.notifications) {
      const c = notificationCounts(r);
      if (f.notifications === "overdue" && !c.overdue) return false;
      if (f.notifications === "outstanding" && !c.outstanding) return false;
    }
    if (q) {
      const hay = [r.affected_name, r.participant_label].map(nameKey).join(" | ");
      if (!hay.includes(q)) return false;
    }
    return true;
  });
};

// --- Raised automatically from an incident ------------------------------------
// Mirrors TRIGGERS in backend core/clinical_report_triggers.py — the incident field,
// the answers that raise a report, and its type. Change both together.
const DECLINES = [
  "Consents to some treatment, but declines some treatment or advice",
  "Declines all treatment or advice",
];
export const AUTO_REPORT_TRIGGERS = [
  { field: "patient_consent", answers: DECLINES, type: "Refusal of treatment or advice" },
  { field: "patient_consent", answers: ["Unable to consent – treated in best interests"],
    type: "Treatment of a patient who lacks capacity" },
  { field: "advised_to_retire", answers: ["Yes, but continued against advice"],
    type: "Continuing despite advice to retire" },
];

/**
 * The report types saving this form would raise automatically, as the server
 * decides it: the field now holds a triggering answer that the saved record
 * (`saved`, or nothing for a new incident) didn't, and the incident has no report
 * of that type already (`existingTypes`, from its listed reports).
 */
export const autoReportsOnSave = (form, saved, existingTypes = []) =>
  AUTO_REPORT_TRIGGERS
    .filter(t => t.answers.includes(form?.[t.field])
              && !t.answers.includes(saved?.[t.field])
              && !existingTypes.includes(t.type))
    .map(t => t.type);
